import React from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import { TablePagination } from "@material-ui/core";
import { useRecoilState } from "recoil";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/solid";

import tableIcons from "../../../../../components/tables/MaterialTableIcons";
import CustomLoader from "../../../../../components/CustomLoader";
import {
  paginationData,
  isPrevPageState,
} from "../../../../../modules/CarrierAgreementMgmt/atoms";

import { useBLPRecentActivities } from "../../api/getBLPRecentActivities";
import { mapRawToActivity, renderActivityMessage } from "./RecentActivityCard";

const EmptySelectIcon = () => <div />;

const EmptyLoadingOverlay = () => <div />;

const RecentActivitiesToolbar = (props: any) => (
  <div className="flex w-full items-center justify-start">
    <MTableToolbar {...props} />
  </div>
);

const PaginationActionsContext =
  React.createContext<React.ComponentType<any> | null>(null);

const PaginationActionsAdapter = ({
  onPageChange,
  ...actionsComponentProps
}: any) => {
  const ActionsComponent = React.useContext(PaginationActionsContext);

  if (ActionsComponent === null) {
    return null;
  }

  return (
    <ActionsComponent {...actionsComponentProps} onChangePage={onPageChange} />
  );
};

const PatchedPagination = ({
  ActionsComponent,
  onChangePage,
  onChangeRowsPerPage,
  ...tablePaginationProps
}: any) => {
  const currentPage = tablePaginationProps.page;

  const [page, setPage] = useRecoilState(paginationData);
  const [isPrevPage, setIsPrevPage] = useRecoilState(isPrevPageState);

  const searchParams = new URLSearchParams(globalThis.location.search);

  const prevPage = searchParams.get("prevPage");
  const actionFrom = searchParams.get("actionFrom");

  React.useEffect(() => {
    if (actionFrom !== null) {
      return;
    }

    let initialPage = currentPage;

    if (prevPage !== null) {
      const previousPage = Number(prevPage);

      const shouldUsePreviousPage =
        isPrevPage || (previousPage === currentPage && previousPage === page);

      if (shouldUsePreviousPage) {
        initialPage = previousPage;
        setIsPrevPage(false);
      }
    }

    setPage(initialPage);
  }, [
    actionFrom,
    currentPage,
    page,
    prevPage,
    isPrevPage,
    setIsPrevPage,
    setPage,
  ]);

  return (
    <PaginationActionsContext.Provider value={ActionsComponent ?? null}>
      <TablePagination
        {...tablePaginationProps}
        onPageChange={onChangePage}
        SelectProps={{
          IconComponent: EmptySelectIcon,
        }}
        onRowsPerPageChange={onChangeRowsPerPage}
        ActionsComponent={PaginationActionsAdapter}
      />
    </PaginationActionsContext.Provider>
  );
};

const StatusBadge = ({ bucket }: { bucket: string }) => {
  const isWarn = bucket === "Warning";

  return (
    <div className="flex items-center gap-1.5">
      {isWarn ? (
        <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-400" />
      ) : (
        <CheckCircleIcon className="h-4 w-4 shrink-0 text-green-500" />
      )}

      <span
        className={`text-xs font-medium ${
          isWarn ? "text-amber-600" : "text-green-600"
        }`}
      >
        {isWarn ? "Warning" : "Success"}
      </span>
    </div>
  );
};

const formatCreatedDate = (createdDate: string | null | undefined): string => {
  if (!createdDate) {
    return "";
  }

  const date = new Date(String(createdDate).replace(" ", "T"));

  if (Number.isNaN(date.getTime())) {
    return String(createdDate);
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const recentActivityColumns = [
  {
    title: "Activity",
    field: "action",
    render: (row: any) => (
      <div className="flex items-center gap-2">
        {row.bucket === "Warning" ? (
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-400" />
        ) : (
          <CheckCircleIcon className="h-4 w-4 shrink-0 text-green-500" />
        )}

        <span className="text-xs text-gray-700">
          {renderActivityMessage(row)}
        </span>
      </div>
    ),
  },
  {
    title: "Status",
    field: "bucket",
    render: (row: any) => <StatusBadge bucket={row.bucket} />,
  },
  {
    title: "Created By",
    field: "displayCreatedBy",
    render: (row: any) => (
      <span className="text-xs text-gray-700">{row.displayCreatedBy}</span>
    ),
  },
  {
    title: "Created Date",
    field: "createdDate",
    render: (row: any) => (
      <span className="text-xs text-gray-500">
        {formatCreatedDate(row.createdDate)}
      </span>
    ),
  },
];

const recentActivityTableComponents = {
  Pagination: PatchedPagination,
  OverlayLoading: EmptyLoadingOverlay,
  Toolbar: RecentActivitiesToolbar,
};

interface ViewAllRecentProps {
  onBackToDashboard?: () => void;
  onRecordClick?: (recordIds: string[]) => void;
}

const ViewAllRecent: React.FC<ViewAllRecentProps> = ({
  onBackToDashboard,
  onRecordClick,
}) => {
  const { data: rawData, isLoading } = useBLPRecentActivities(2000);

  const rows = React.useMemo(
    () => (rawData ?? []).map(mapRawToActivity),
    [rawData],
  );

  const handleRowClick = React.useCallback(
    (_event?: React.MouseEvent<Element, MouseEvent>, rowData?: any) => {
      const recordIds = Array.isArray(rowData?.recordIds)
        ? rowData.recordIds
        : [];

      if (recordIds.length === 0 || onRecordClick === undefined) {
        return;
      }

      onRecordClick(recordIds);
    },
    [onRecordClick],
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="flex items-center justify-between border-b border-[#E8EDF5] bg-white px-2 py-2">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#009AFF]">
            <CheckCircleIcon className="h-[18px] w-[18px] text-white" />
          </div>

          <div>
            <div className="mb-px text-[11px] font-medium text-gray-500">
              Business Licenses &amp; Permits
            </div>

            <h1 className="m-0 text-[15px] font-bold uppercase text-primary">
              Recent Activities
            </h1>
          </div>
        </div>

        {onBackToDashboard && (
          <button
            type="button"
            onClick={onBackToDashboard}
            className="group inline-flex h-7 items-center rounded-md border border-primary px-3 py-1 text-xs font-semibold text-primary shadow-md transition duration-500 hover:scale-90 hover:bg-primary hover:text-white"
          >
            <span>Back to Dashboard</span>

            <span className="ml-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary transition group-hover:bg-white">
              <ArrowRightIcon
                className="h-3 w-3 rotate-180 transform text-white group-hover:text-primary"
                aria-hidden="true"
              />
            </span>
          </button>
        )}
      </div>

      <div className="p-2">
        <MaterialTable
          title="Recent Activities"
          icons={tableIcons}
          columns={recentActivityColumns as any}
          data={rows as any}
          isLoading={isLoading}
          onRowClick={handleRowClick}
          localization={{
            body: {
              emptyDataSourceMessage: isLoading ? (
                <CustomLoader />
              ) : (
                <span
                  style={{
                    fontSize: "14px",
                  }}
                >
                  No records to display.
                </span>
              ),
            },
          }}
          options={{
            showTitle: false,
            search: false,
            searchFieldAlignment: "left",
            toolbar: false,
            padding: "dense",
            paging: true,
            pageSize: 10,
            pageSizeOptions: [10, 20, 30, 50],
            headerStyle: {
              backgroundColor: "#fff",
              color: "#203A75",
              fontWeight: "bold",
              fontSize: "12px",
            },
            rowStyle: {
              fontSize: "13px",
              color: "#000",
            },
          }}
          components={recentActivityTableComponents}
        />
      </div>
    </div>
  );
};

export default ViewAllRecent;
